import { Box, Chip, Collapse, IconButton, Typography } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { Edit, ExpandLess, ExpandMore } from '@mui/icons-material';
import { setAvailabilityOverride } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService, formatTimeRangeWithMeridiem } from '@uplift/services';
import { WeekDay, WeekDayKeys } from '@uplift/types';
import React from 'react';
import { useAlert } from '../../../AlertProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useToggleObject } from '../../../hooks';
import { LoadableTypography } from '../../../LoadableTypography';
import { useAvailabilityData } from './Availability.hooks';
import { EditAvailabilityOverrideModal } from './EditAvailabilityOverrideModal';


export type AvailabilityProps = {
  today: string;
  date: string;
};


const InfoChip = withStyles(Chip, theme => ({
  root: {
    color: theme.palette.info.dark,
    background: theme.palette.info.background,
  }
}));


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0.5, 2),
    backgroundColor: theme.palette.grey[50],
  },
  general: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    cursor: 'pointer',
    userSelect: 'none',
    color: theme.palette.text.secondary,
  },
}));


export const Availability: React.FC<AvailabilityProps> = (props) => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { classes } = useStyles();
  const timezone = useTimezone();
  const [ isEditAvailability, setIsEditAvailability ] = React.useState(false);
  const { value: opened, toggle: toggleOpened } = useToggleObject(false);
  const date = React.useMemo(() => CalendarDateService.date(props.date, DateFormats.ISO_DATE), [ props.date ]);
  const today = React.useMemo(() => CalendarDateService.date(props.today, DateFormats.ISO_DATE), [ props.today ]);

  const { data, isLoading } = useAvailabilityData(props.date, timezone);

  const hasOverrides = React.useMemo(() => {
    return !!data?.overrides?.[props.date];
  }, [ props.date, data?.overrides ]);

  const weekdayKey = React.useMemo(() => {
    const weekday = CalendarDateService.date.tz(props.date, DateFormats.ISO_DATE, timezone).day();
    const key = WeekDay[weekday] as WeekDayKeys;

    return key;
  }, [ props.date, timezone ]);

  const availability = React.useMemo(() => {
    const template = data?.template?.[weekdayKey];
    const override = data?.overrides?.[props.date];

    return override || template || [];
  }, [ timezone, props.date, data?.overrides, data?.template ]); // eslint-disable-line react-hooks/exhaustive-deps

  const ranges = React.useMemo(() => {
    return availability.map(r => {
      return formatTimeRangeWithMeridiem(
        CalendarDateService.time(r.start).tz(timezone, true),
        CalendarDateService.time(r.end).tz(timezone, true),
        { separator: '–', forceMinutes: false }
      );
    }).join(', ');
  }, [ availability, timezone ]);

  const isEditDisabled = React.useMemo(() => {
    return date.isBefore(today, 'date') || isLoading;
  }, [ today, date, isLoading ]);

  const title = React.useMemo(() => {
    if (availability.length || isLoading) {
      return (
        <Box className={classes.title} onClick={toggleOpened}>
          <LoadableTypography loading={isLoading} width="158px" color="textPrimary">Available to new clients</LoadableTypography>
          {opened ? <ExpandLess fontSize="small" color="inherit" /> : <ExpandMore fontSize="small" color="inherit" />}
        </Box>
      );
    }

    return (
      <Box className={classes.title}>
        <Typography color="textPrimary">Not available to new clients</Typography>
      </Box>
    );
  }, [ availability, opened, classes.title, toggleOpened, isLoading ]);

  return (
    <Box className={classes.root}>
      <Box className={classes.general}>
        {hasOverrides && <Box mr={1}><InfoChip size="small" label="Override" /></Box>}
        {title}
        <Box>
          <IconButton disabled={isEditDisabled} color="secondary" onClick={() => setIsEditAvailability(true)}>
            <Edit />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={opened}>
        <Box>
          <Typography variant="body2" color="textPrimary">
            {ranges}
          </Typography>
        </Box>
      </Collapse>
      <EditAvailabilityOverrideModal
        open={isEditAvailability}
        onClose={() => setIsEditAvailability(false)}
        date={props.date}
        availability={availability}
        defaultAvailability={data?.template?.[weekdayKey]}
        onAvailabilityChange={async (ranges, date) => {
          await setAvailabilityOverride({ date, ranges });
          await queryClient.invalidateQueries([ 'calendar', date, 'availability' ]);
          alert.success('Availability updated');
        }}
      />
    </Box>
  );
};
